import { type ProductTag } from '@debijenkorf/product-item';

import { priceMapper, type PriceMapperInput, type PriceMapperProps } from './priceMapper';
import { createMapper } from '../dataMapper';

import type { Brand, SigningsV2 } from '@/types/api/product';
import { GIFT_SIGNING } from '@/utilities/constants';
import { getRelativeUrl } from '@/utilities/getRelativeUrl';

export type CarouselProductMapperInput = {
  code: string;
  displayName: string;
  name?: string;
  brand?: Brand;
  subBrand?: Brand;
  color?: string;
  url: string;
  selection: { sku: string };
  primaryImage: { url: string };
  secondaryImage?: { url: string };
  price: PriceMapperInput['price'];
  signings?: SigningsV2;
  categoryPath?: string[];
  trackingMetadata?: { rtmAttributionId?: string; purchasable?: boolean };
};

export type CarouselProductMapperProps = {
  code: string;
  sku: string;
  displayName: string;
  name?: string;
  brand?: Brand;
  subBrand?: Brand;
  color?: string;
  url: string;
  images: { mainImage: string; secondaryImage?: string; alt: string };
  price: PriceMapperProps;
  tags?: ProductTag[];
  hasGift: boolean;
  isSponsored: boolean;
  discountSigning?: string;
  tracking: { discount?: number; categoryPath?: string[]; purchasable?: boolean };
};

const isGiftSigning = (signing: string) => signing === GIFT_SIGNING;

export const carouselProductMapper = createMapper<
  CarouselProductMapperInput,
  CarouselProductMapperProps
>(
  ({
    code,
    displayName,
    name,
    brand,
    subBrand,
    color,
    url,
    selection,
    primaryImage,
    secondaryImage,
    price,
    signings,
    categoryPath,
    trackingMetadata,
  }) => {
    const priceProps = priceMapper.select({ price, signings });
    const discount = priceProps.originalPrice
      ? priceProps.originalPrice - priceProps.sellingPrice
      : undefined;

    return {
      code,
      sku: selection.sku,
      displayName,
      name,
      brand,
      subBrand,
      color,
      url: getRelativeUrl(url),
      images: {
        mainImage: primaryImage.url,
        secondaryImage: secondaryImage?.url,
        alt: displayName,
      },
      price: priceProps,
      tags: signings?.merchandise
        ?.filter(label => !isGiftSigning(label))
        .map(label => ({ label, variant: 'merchandisingTransparent' })),
      hasGift: Boolean(signings?.merchandise?.some(isGiftSigning)),
      isSponsored: Boolean(trackingMetadata?.rtmAttributionId),
      discountSigning: signings?.discount,
      tracking: {
        discount,
        categoryPath,
        purchasable: trackingMetadata?.purchasable,
      },
    };
  },
);
